import React, { useEffect, useState } from "react";
import { ISafe, ISecurityDetails } from "../types";
import AddButton from "../../../../components/AddButton";
import { useGetSafeDetailsByRecordNumber } from "../hooks/useGetSafeDetailsByRecordNumber";
import SafeDetailsCard from "../../../../components/SafeDetailsCard";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  SafeAddState,
  SafeSaving,
  SecurityAddState,
  SecurityEditState,
  SafeModalState,
  SafeForEdit,
  safeDeactivateModal,
} from "../atoms/atoms";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import { useQueryClient } from "react-query";
import Modal from "../../../../components/Modal";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import CancelButton from "../../../../components/CancelButton";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import MinusButton from "../../../../components/MinusButton";
import PlusButton from "../../../../components/PlusButton";
import SelectHookFormSingle from "../../../../components/SelectHookFormSingle";
import TextInputLabelHookForm from "../../../../components/TextInputLabelHookForm";
import { useSafeType } from "../hooks/useSafeType";
import { useSafeMakes } from "../hooks/useSafeMakes";
import useAddSingleSafe from "../hooks/useAddSingleSafe";
import { toast } from "../../../../components/toastNotification";
import useEditSingleSafe from "../hooks/useEditSingleSafe";
import CheckBoxLabelHookForm from "../../../../components/CheckBoxLabelHookForm";
import useDeleteSafe from "../hooks/useDeleteSafe";
import ConfirmBoxDelete from "../../../../components/ConfirmBoxDelete";
import CloseIconButton from "../../../../components/CloseIconButton";
import SecurityModal from "../../../../components/SecurityModal";

interface SafeProps {
  safes?: Array<ISafe>;
  rowForEdit: ISecurityDetails | undefined;
}
const defaultSafe = [
  {
    parentRow: true,
    tableName: "safe",
    safeTypeId: null,
  },
] as Array<ISafe>;

const SafeForm = ({
  rowForEdit,
  safes = defaultSafe,
}: SafeProps): JSX.Element => {
  const [isShowSafeModal, setShowSafeModal] = useRecoilState(SafeAddState);
  const [showSafeCardModal, setShowSafeCardModal] =
    useRecoilState(SafeModalState);
  const [isAddState] = useRecoilState(SecurityAddState);
  const [isEditState] = useRecoilState(SecurityEditState);
  const [isAddInEditState, setIsAddInEditState] = useState(false);
  const [isSaving, setIsSaving] = useRecoilState(SafeSaving);
  const [showDeactivateModal, setShowDeactivateModal] =
    useRecoilState(safeDeactivateModal);
  const [hasDeletePermission, setHasDeletePermission] = useState(false);
  const [forDeleteDetails, setForDeleteDetails] = useState();
  const queryClient = useQueryClient();
  const [hasFirstFieldValue, setHasFirstFieldValue] = useState(true);
  const [clickCount, setClickCount] = useState(0);
  const [safeForEdit, setSafeForEditData] = useRecoilState<any>(SafeForEdit);

  //Permission
  const userPermissions = useRecoilValue(permissionsSelector);
  const addPermission = PERMISSIONS.permission_security_add;
  const deletePermission = PERMISSIONS.permission_security_delete;

  //#region Form
  const {
    register,
    formState: { errors },
    control,
    watch,
  } = useFormContext();

  const formMethods = useFormContext();
  //#endregion
  //#region Add/minus of forms
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "safe",
  });

  const tableArray = safes.length > 0 && watch(`${safes[0].tableName}`);

  const controlledFields =
    tableArray &&
    fields.map((field, index) => ({
      ...field,
      ...tableArray[index],
    }));

  //SETTING DEFAULT TABLE VALUES
  useEffect(() => {
    if (safes) {
      replace(safes);
    }
  }, [safes]);
  //#endregion Add/minus
  //#region Options
  const { data: safeTypes } = useSafeType();
  const safeTypeOption = safeTypes?.data?.map(
    (x: { id: number; safeType: string }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.safeType,
      };
    }
  );
  const { data: SafeMakes } = useSafeMakes();
  const safeMakesOption = SafeMakes?.data?.map(
    (x: { id: number; safeMake: string }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.safeMake,
      };
    }
  );
  const yesOrNoOption = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];
  //#endregion
  //Add Validation
  const checkEmptyFields = (buttonState) => {
    if (buttonState === "add") {
      setClickCount(clickCount + 1);
      watch("safe[0].safeTypeId")
        ? setHasFirstFieldValue(true)
        : setHasFirstFieldValue(false);
    }
    if (buttonState === "proceed") {
      const fieldLen = controlledFields.length;
      let hasValueCount = 0;

      controlledFields.forEach((obj: ISafe, i: number) =>
        Object.entries(obj)?.forEach(([key, value]) => {
          if (key === "safeTypeId") {
            if (value) {
              hasValueCount = hasValueCount + 1;
              if (fieldLen === hasValueCount) {
                setShowSafeModal(false);
              }
            } else {
              setClickCount(clickCount + 1);
              toast.fire(
                "Information",
                "Please fill the Safe Type to proceed.",
                "info"
              );
              setHasFirstFieldValue(false);
            }
          }
        })
      );
    }
    if (buttonState === "saveEdit") {
      setClickCount(clickCount + 1);
      watch("safeTypeId")
        ? handleEditSave()
        : toast.fire(
            "Information",
            "Please fill the Safe Type to save.",
            "info"
          );
    }
  };

  useEffect(() => {
    !hasFirstFieldValue &&
      toast.fire(
        "Information",
        "Please fill the Safe Type to add new.",
        "info"
      );
  }, [clickCount]);
  //Single Add Initialization
  const {
    mutate: addSingleSafe,
    isSuccess: addSafeSuccess,
    data: addSafeResult,
    reset: resetAddSafeData,
  } = useAddSingleSafe();
  //Single Edit Initialization
  const {
    mutate: editSingleSafe,
    isSuccess: editSafeSuccess,
    data: editSafeResult,
    reset: resetEditSafeData,
  } = useEditSingleSafe();
  //Delete Initialization
  const {
    mutate: deleteSafe,
    isSuccess: deleteSafeSuccess,
    data: deleteSafeResult,
    reset: resetDeleteSafeData,
  } = useDeleteSafe();
  //Reset form data sing Add
  const resetData = () => {
    formMethods.setValue("safe[0].safeTypeId", "");
    formMethods.setValue("safe[0].isSafeBolted", "");
    formMethods.setValue("safe[0].safeCode", "");
    formMethods.setValue("safe[0].safeMakeId", "");
    formMethods.setValue("safe[0].safeModel", "");
    formMethods.setValue("safe[0].safeSerialNumber", "");
    formMethods.setValue("safe[0].safeLocation", "");
  };

  //Save Get values for single add
  const handleAddSave = () => {
    setIsSaving(true);
    const safeData = {
      recordNumber: rowForEdit?.recordNumber,
      safeTypeId: formMethods.getValues("safe[0].safeTypeId"),
      isSafeBolted: formMethods.getValues("safe[0].isSafeBolted"),
      safeCode: formMethods.getValues("safe[0].safeCode"),
      safeMakeId: formMethods.getValues("safe[0].safeMakeId"),
      safeModel: formMethods.getValues("safe[0].safeModel"),
      safeSerialNumber: formMethods.getValues("safe[0].safeSerialNumber"),
      safeLocation: formMethods.getValues("safe[0].safeLocation"),
    };
    addSingleSafe(safeData);
    setShowSafeModal(false);
  };
  //Set Edit default values
  const setEditSafeValues = (safe) => {
    formMethods.setValue("id", safe.id);
    formMethods.setValue("safeTypeId", safe.safeTypeId);
    formMethods.setValue("isSafeBolted", safe.isSafeBolted);
    formMethods.setValue("safeCode", safe.safeCode);
    formMethods.setValue("safeMakeId", safe.safeMakeId);
    formMethods.setValue("safeModel", safe.safeModel);
    formMethods.setValue("safeSerialNumber", safe.safeSerialNumber);
    formMethods.setValue("safeLocation", safe.safeLocation);
    formMethods.setValue("isActive", safe.isActive);
    formMethods.setValue("createdBy", safe.createdBy);
    formMethods.setValue("createdDate", safe.createdDate);

    queryClient.invalidateQueries("GetSafeDetailsByRecordNumber");
    setShowSafeCardModal(true);
  };

  //Save Edit single safe
  const handleEditSave = () => {
    setIsSaving(true);
    const safeNewData = {
      recordNumber: rowForEdit?.recordNumber,
      id: formMethods.getValues("id"),
      safeTypeId: formMethods.getValues("safeTypeId"),
      isSafeBolted: formMethods.getValues("isSafeBolted"),
      safeCode: formMethods.getValues("safeCode"),
      safeMakeId: formMethods.getValues("safeMakeId"),
      safeModel: formMethods.getValues("safeModel"),
      safeSerialNumber: formMethods.getValues("safeSerialNumber"),
      safeLocation: formMethods.getValues("safeLocation"),
      isActive: formMethods.getValues("isActive"),
      createdBy: formMethods.getValues("createdBy"),
      createdDate: formMethods.getValues("createdDate"),
    };
    const safeOldData = {
      recordNumber: safeForEdit?.recordNumber,
      id: safeForEdit?.id,
      safeTypeId: safeForEdit?.safeTypeId,
      isSafeBolted: safeForEdit?.isSafeBolted,
      safeCode: safeForEdit?.safeCode,
      safeMakeId: safeForEdit?.safeMakeId,
      safeModel: safeForEdit?.safeModel,
      safeSerialNumber: safeForEdit?.safeSerialNumber,
      safeLocation: safeForEdit?.safeLocation,
      isActive: safeForEdit?.isActive,
      createdBy: safeForEdit?.createdBy,
      createdDate: safeForEdit?.createdDate,
    };
    const newData = JSON.stringify(safeNewData);
    const oldData = JSON.stringify(safeOldData);
    if (newData === oldData) {
      toast.fire("Saving Failed", "No changes have been made.", "warning");
      setIsSaving(false);
    } else {
      setIsSaving(true);
      editSingleSafe(safeNewData);
      setShowSafeCardModal(false);
    }
  };
  //Deactivate
  const handleDeactivate = () => {
    deleteSafe(forDeleteDetails);
    setShowDeactivateModal(false);
  };
  const deleteMessage = (
    <div className="text-gray-500 font-normal">
      {"Are you sure you want to deactivate this Safe?"}
    </div>
  );
  useEffect(() => {
    if (userPermissions.includes(deletePermission)) {
      setHasDeletePermission(true);
    }
  }, []);
  //Toasters
  if (addSafeSuccess && addSafeResult) {
    if (addSafeResult.status === 201 || addSafeResult.status === 200) {
      toast.fire("Success", "Safe added successfully.", "success");
      queryClient.invalidateQueries("GetSafeDetailsByRecordNumber");
    }
    setIsSaving(false);
    resetAddSafeData();
    resetData();
  }
  if (editSafeSuccess && editSafeResult) {
    if (editSafeResult.status === 201 || editSafeResult.status === 200) {
      toast.fire("Success", "Safe updated successfully.", "success");
      queryClient.invalidateQueries("GetSafeDetailsByRecordNumber");
    }
    setIsSaving(false);
    resetEditSafeData();
  }
  if (deleteSafeSuccess && deleteSafeResult) {
    if (deleteSafeResult.status === 201 || deleteSafeResult.status === 200) {
      toast.fire("Success", "Safe deactivated successfully.", "success");
      queryClient.invalidateQueries("GetSafeDetailsByRecordNumber");
    }
    resetDeleteSafeData();
  }
  const { data: safesData } = useGetSafeDetailsByRecordNumber(
    rowForEdit ? rowForEdit?.recordNumber : 0
  );
  return (
    <div className="mx-2 mt-2 bg-white box-shadow rounded-lg h-full">
      <div className="flex flex-row justify-between py-2">
        <div className="text-quinary font-bold font-sans px-4">
          Safe Details Section
        </div>
        {userPermissions.includes(addPermission) ? (
          <div className="py-2 px-4">
            <AddButton
              className={"opacity-100"}
              disabled={
                safesData !== undefined
                  ? safesData.length >= 5
                    ? true
                    : false
                  : false
              }
              buttonText="Add Safe"
              onClick={() => {
                setShowSafeModal(true);
                setIsAddInEditState(true);
              }}
            />
          </div>
        ) : (
          <div className="py-5 px-4"></div>
        )}
      </div>
      {isAddState && (
        <div className="text-quinary font-bold font-sans px-4">
          <h1
            className="mr-auto truncate w-full cursor-pointer text-ellipsis font-bold text-primary underline text-lg uppercase"
            onClick={() => {
              setShowSafeModal(true);
            }}
          >
            {controlledFields &&
              controlledFields[0]?.safeTypeId &&
              "View Safe/s"}
          </h1>
        </div>
      )}

      {/* Show card edit button if edit state*/}
      {isEditState && (
        <div className="w-full borderContainer -mt-4 pb-4 bg-white px-4">
          <div className="grid gap-2 grid-cols-5">
            {/* Card */}
            {safesData?.map((safe) => (
              <div key={safe.id}>
                <SafeDetailsCard
                  safe={safe}
                  isEditState={isEditState}
                  onClickView={() => {}}
                  onClickEdit={() => {
                    setEditSafeValues(safe);
                    setSafeForEditData(safe);
                  }}
                  onClickDelete={() => {
                    setShowDeactivateModal(true);
                    setForDeleteDetails(safe);
                  }}
                  hasDeletePermission={hasDeletePermission}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Add Safe Form */}
      {isShowSafeModal && (
        <SecurityModal
          isCloseHide={true}
          modalFilterStat=""
          className=""
          width={""}
          title={""}
          isOpen={isShowSafeModal}
          onClose={undefined}
          hasCloseButton={false}
        >
          <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
            <div className="ml-2 font-bold text-primary text-md">
              {isAddState || isAddInEditState ? "Add Safe" : "Edit Safe"}
            </div>
            {isAddState ? (
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="button"
                  disabled={false}
                  buttonText={"Proceed"}
                  className={undefined}
                  onClick={() => {
                    checkEmptyFields("proceed");
                    setHasFirstFieldValue(true);
                  }}
                />
                {/* <CancelButton
                  onClick={() => {
                    setShowSafeModal(false);
                    resetData();
                    remove([1, 2, 3, 4]);
                  }}
                /> */}
                <div className="relative -top-2 -right-6">
                  <CloseIconButton onClick={() => setShowSafeModal(false)} />
                </div>
              </div>
            ) : (
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="button"
                  disabled={false}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={() => {
                    watch("safe[0].safeTypeId")
                      ? handleAddSave()
                      : toast.fire(
                          "Information",
                          "Please fill the safe field to save.",
                          "info"
                        );
                  }}
                />
                <CancelButton
                  onClick={() => {
                    setShowSafeModal(false);
                    resetData();
                  }}
                />
              </div>
            )}
          </div>
          <div className="w-full border-b-2 border-secondary mb-3"></div>
          <div className="text-xs text-secondary italic font-normal m-2">
            <b> Note:</b> All fields with * are required to fill.
          </div>
          <div className="box-shadow rounded-md modal-content">
            {controlledFields &&
              controlledFields.map((field, index) => {
                return (
                  <>
                    <div className="ml-4 py-2" key={field.id}>
                      {" "}
                      {index > 0 ? (
                        <MinusButton
                          type="button"
                          onClick={() => remove(index)}
                        />
                      ) : (
                        !isEditState && (
                          <PlusButton
                            type="button"
                            onClick={(e) => {
                              if (controlledFields.length < 5) {
                                checkEmptyFields("add");
                                watch(`safe[0].safeTypeId`) &&
                                  append({
                                    tableName: safes[0]?.tableName,
                                    parentRow: false,
                                  });
                              }
                            }}
                          />
                        )
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 mx-4 gap-6 pb-4">
                      <div className="space-y-1">
                        <Controller
                          name={`${safes[0].tableName}[${index}].safeTypeId`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                              }}
                              value={field.value}
                              options={safeTypeOption}
                              label="Safe Type"
                              className={
                                formMethods.formState.errors.safeTypeId &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={true}
                            />
                          )}
                        />
                        <Controller
                          name={`${safes[0].tableName}[${index}].isSafeBolted`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                              }}
                              value={field.value}
                              options={yesOrNoOption}
                              label="Is Safe Bolted to the Floor"
                              className={
                                formMethods.formState.errors.isSafeBolted &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={false}
                            />
                          )}
                        />
                        <TextInputLabelHookForm
                          name={`${safes[0].tableName}[${index}].safeCode`}
                          label="Safe Code"
                          type="text"
                          register={formMethods.register(
                            `${safes[0].tableName}[${index}].safeCode`,
                            {}
                          )}
                          maxLength={100}
                        />
                        <TextInputLabelHookForm
                          name={`${safes[0].tableName}[${index}].safeModel`}
                          label="Safe Model"
                          type="text"
                          register={formMethods.register(
                            `${safes[0].tableName}[${index}].safeModel`,
                            {}
                          )}
                          maxLength={100}
                        />
                      </div>
                      <div className="space-y-1">
                        <Controller
                          name={`${safes[0].tableName}[${index}].safeMakeId`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                              }}
                              value={field.value}
                              options={safeMakesOption}
                              label="Safe Make"
                              className={
                                formMethods.formState.errors.safeMakeId &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={false}
                            />
                          )}
                        />
                        <TextInputLabelHookForm
                          name={`${safes[0].tableName}[${index}].safeSerialNumber`}
                          label="Safe Serial Number"
                          type="text"
                          register={formMethods.register(
                            `${safes[0].tableName}[${index}].safeSerialNumber`,
                            {}
                          )}
                          maxLength={100}
                        />
                        <TextInputLabelHookForm
                          name={`${safes[0].tableName}[${index}].safeLocation`}
                          label="Safe Location"
                          type="text"
                          register={formMethods.register(
                            `${safes[0].tableName}[${index}].safeLocation`,
                            {}
                          )}
                          maxLength={20}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="w-full border-b-2 border-grey-200"></div>
                    </div>
                  </>
                );
              })}
          </div>
        </SecurityModal>
      )}
      {/* Edit Safe Card Form */}
      {showSafeCardModal && (
        <Modal
          isCloseHide={true}
          modalFilterStat=""
          className=""
          width={""}
          title={""}
          isOpen={showSafeCardModal}
          onClose={undefined}
          hasCloseButton={false}
        >
          <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
            <div className="ml-2 font-bold text-primary text-md">
              {isAddState ? "Add Safe" : "Edit Safe"}
            </div>
            <div className="flex justify-between gap-2 mr-2">
              <SubmitHookForm
                type="button"
                disabled={false}
                buttonText={isSaving ? "Saving.." : "Save"}
                className={undefined}
                onClick={() => {
                  checkEmptyFields("saveEdit");
                }}
              />
              <CancelButton
                onClick={() => {
                  setShowSafeCardModal(false);
                }}
              />
            </div>
          </div>
          <div className="w-full border-b-2 border-secondary mb-3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 mx-4 gap-6 pb-4">
            <div className="space-y-1">
              <Controller
                name="safeTypeId"
                control={formMethods.control}
                rules={{ required: true }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                    }}
                    value={field.value}
                    options={safeTypeOption}
                    label="Safe Type"
                    className={
                      formMethods.formState.errors.safeTypeId &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={true}
                  />
                )}
              />
              <Controller
                name="isSafeBolted"
                control={formMethods.control}
                rules={{ required: false }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                    }}
                    value={field.value}
                    options={yesOrNoOption}
                    label="Is Safe Bolted to the Floor"
                    className={
                      formMethods.formState.errors.isSafeBolted &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={false}
                  />
                )}
              />
              <TextInputLabelHookForm
                name="safeCode"
                label="Safe Code"
                type="text"
                register={formMethods.register("safeCode", {})}
                maxLength={100}
              />
              <TextInputLabelHookForm
                name="safeModel"
                label="Safe Model"
                type="text"
                register={formMethods.register("safeModel", {})}
                maxLength={100}
              />
            </div>
            <div className="space-y-1">
              <Controller
                name="safeMakeId"
                control={formMethods.control}
                rules={{ required: false }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                    }}
                    value={field.value}
                    options={safeMakesOption}
                    label="Safe Make"
                    className={
                      formMethods.formState.errors.safeMakeId &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={false}
                  />
                )}
              />
              <TextInputLabelHookForm
                name="safeSerialNumber"
                label="Safe Serial Number"
                type="text"
                register={formMethods.register("safeSerialNumber", {})}
                maxLength={100}
              />
              <TextInputLabelHookForm
                name="safeLocation"
                label="Safe Location"
                type="text"
                register={formMethods.register("safeLocation", {})}
                maxLength={20}
              />
              {isEditState && hasDeletePermission && (
                <CheckBoxLabelHookForm
                  name="isActive"
                  type="checkbox"
                  register={register}
                  label="Active Account"
                  className="cursor-pointer auto-rows-auto text-right justify-items-center"
                />
              )}
            </div>
          </div>
        </Modal>
      )}
      {/* Deactivate Modal */}
      {showDeactivateModal && (
        <ConfirmBoxDelete
          ModalTitle="Security Safe"
          qConfirmation={deleteMessage}
          isOpen={showDeactivateModal}
          onClose={() => setShowDeactivateModal(false)}
          onClickFunction={() => handleDeactivate()}
          cancelButtonRef={undefined}
        />
      )}
    </div>
  );
};

export default SafeForm;
